import { sendEmail } from './emailService';

const USERS_KEY = 'flare_store_users';
const SESSION_KEY = 'flare_store_session';
const USER_ORDERS_KEY_PREFIX = 'flare_user_orders_';
const USER_SUBS_KEY_PREFIX = 'flare_user_subs_';
const USER_INV_KEY_PREFIX = 'flare_user_inv_';

function randomId(prefix = 'id') {
  return prefix.toUpperCase() + '-' + Math.random().toString(36).slice(2, 8).toUpperCase() + '-' + Date.now().toString(36).toUpperCase().slice(-5);
}

function hashPassword(pw) {
  let hash = 5381;
  const str = String(pw || '');
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return 'h_' + Math.abs(hash).toString(36);
}

function nowISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString();
}

function readJSON(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJSON(key, val) {
  try {
    window.localStorage.setItem(key, JSON.stringify(val));
  } catch {}
}

function normUser(u) {
  if (!u || typeof u !== 'object') return null;
  return {
    ...u,
    id: String(u.id || randomId('USR')),
    name: String(u.name || u.full_name || u.display_name || 'Customer').trim(),
    email: String(u.email || '').trim().toLowerCase(),
    phone: String(u.phone || u.mobile || u.whatsapp || '').trim(),
    address: String(u.address || u.delivery_address || ''),
    city: String(u.city || 'Lahore'),
    role: u.role === 'admin' ? 'admin' : 'customer',
    status: String(u.status || 'active'),
    banned: Boolean(u.banned || u.status === 'banned'),
    newsletter_optin: u.newsletter_optin !== undefined ? Boolean(u.newsletter_optin) : true,
    whatsapp_updates: u.whatsapp_updates !== undefined ? Boolean(u.whatsapp_updates) : true,
    password_hash: String(u.password_hash || u.passwordHash || ''),
    source: String(u.source || 'register'),
    created_at: u.created_at || nowISO(),
    updated_at: nowISO(),
  };
}

export function clearLocalCustomerSeeds() {
  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (
        k &&
        (k.startsWith(USER_ORDERS_KEY_PREFIX) ||
          k.startsWith(USER_SUBS_KEY_PREFIX) ||
          k.startsWith(USER_INV_KEY_PREFIX))
      ) {
        keys.push(k);
      }
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {}
}

export async function seedUserDataFor() {
  return null;
}

export const userService = {
  list() {
    const list = readJSON(USERS_KEY, []);
    return Array.isArray(list) ? list.map(normUser).filter(Boolean) : [];
  },
  get(id) {
    return this.list().find(u => String(u.id) === String(id)) || null;
  },
  getByEmail(email) {
    return this.list().find(u => String(u.email).toLowerCase() === String(email || '').trim().toLowerCase()) || null;
  },
  getByEmailOrPhone(email, phone) {
    const e = String(email || '').trim().toLowerCase();
    const p = String(phone || '').trim();
    return this.list().find(u =>
      (e && String(u.email).toLowerCase() === e) ||
      (p && String(u.phone).replace(/\D/g, '') === p.replace(/\D/g, ''))
    ) || null;
  },
  async create(payload) {
    const list = this.list();
    if (!payload.email) throw new Error('Email is required');
    const email = String(payload.email).toLowerCase().trim();
    if (list.find(u => String(u.email).toLowerCase() === email)) {
      const err = new Error('An account with this email already exists. Please log in instead.');
      err.code = 'EMAIL_TAKEN';
      throw err;
    }
    const phoneDigits = String(payload.phone || '').replace(/\D/g, '');
    if (phoneDigits && list.find(u => String(u.phone || '').replace(/\D/g, '') === phoneDigits)) {
      const err = new Error('An account with this phone number already exists. Please log in instead.');
      err.code = 'PHONE_TAKEN';
      throw err;
    }
    const norm = normUser({
      ...payload,
      id: randomId('USR'),
      password_hash: payload.password_hash || (payload.password ? hashPassword(payload.password) : ''),
    });
    list.push(norm);
    writeJSON(USERS_KEY, list);
    return norm;
  },
  update(id, payload) {
    const list = this.list();
    const idx = list.findIndex(u => String(u.id) === String(id));
    if (idx < 0) throw new Error('User not found');
    const updated = normUser({ ...list[idx], ...payload, id, password_hash: list[idx].password_hash });
    list[idx] = updated;
    writeJSON(USERS_KEY, list);
    return updated;
  },
  remove(id) {
    const list = this.list().filter(u => String(u.id) !== String(id));
    writeJSON(USERS_KEY, list);
    try { window.localStorage.removeItem(USER_ORDERS_KEY_PREFIX + id); } catch {}
    try { window.localStorage.removeItem(USER_SUBS_KEY_PREFIX + id); } catch {}
    try { window.localStorage.removeItem(USER_INV_KEY_PREFIX + id); } catch {}
    return true;
  },
  async ban(id, reason = 'Administrative ban') {
    const existing = this.get(id);
    if (existing?.email) {
      try {
        await sendEmail({
          to: existing.email,
          template: 'ACCOUNT_BANNED',
          data: {
            name: existing.name || 'Customer',
            email: existing.email,
            reason,
          },
        });
      } catch {}
    }
    const u = this.update(id, { banned: true, status: 'banned', ban_reason: reason });
    if (String(window.localStorage.getItem(SESSION_KEY) || '').includes(`"id":"${id}"`)) {
      try { window.localStorage.removeItem(SESSION_KEY); } catch {}
    }
    return u;
  },
  unban(id) {
    return this.update(id, { banned: false, status: 'active' });
  },
  changePassword(id, newPassword) {
    const list = this.list();
    const idx = list.findIndex(u => String(u.id) === String(id));
    if (idx < 0) throw new Error('User not found');
    list[idx].password_hash = hashPassword(newPassword);
    writeJSON(USERS_KEY, list);
    return true;
  },
  verifyPassword(userOrId, password) {
    let u;
    if (typeof userOrId === 'object') u = userOrId;
    else u = this.get(userOrId);
    if (!u) return false;
    return String(u.password_hash) === hashPassword(password);
  },
  getSession() {
    const raw = readJSON(SESSION_KEY, null);
    if (!raw || !raw?.id) return null;
    const u = this.get(raw.id);
    if (!u || u.banned) return null;
    return u;
  },
  setSession(user) {
    writeJSON(SESSION_KEY, { id: user.id, at: Date.now() });
    return user;
  },
  clearSession() {
    try { window.localStorage.removeItem(SESSION_KEY); } catch {}
  },
  getSubscriptions() {
    return [];
  },
  getOrders() {
    return [];
  },
  getInvoices() {
    return [];
  },
  async ensureSeeded() {
    return null;
  },
};

export default userService;
